import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useParams, Link } from "react-router-dom";
import { sanitizeEmailHtml } from "../lib/sanitizeHtml";
import {
  Inbox as InboxIcon, Star, Clock, Tag, Send, FileEdit,
  OctagonAlert, Trash2, Mail as MailIcon, MailOpen, Plus, Search,
  Reply, X, Paperclip, RefreshCw, ChevronLeft, Wand2,
  Tag as TagIcon, Megaphone, ExternalLink, AtSign, Loader2,
  ShieldCheck, ArchiveRestore, ShieldX, Check, ChevronRight,
} from "lucide-react";
import { Avatar, Badge, Button, EmptyState, Modal, ModalFooter, Skeleton, Tooltip, useToast } from "@/components/ui";
import { RichEmailEditor } from "@/components/RichEmailEditor";
import { SnippetPicker } from "@/features/snippets/SnippetPicker";
import { api, ApiError } from "@/lib/api";
import { formatRelative } from "@/lib/format";
import { cn } from "@/lib/utils";

// ─── Types ─────────────────────────────────────────────────────────────────

interface Label {
  id: string;
  name: string;
  color: string;
  is_system: boolean;
  icon: string | null;
  position: number;
  thread_count: number;
  unread_count: number;
}

/** Mail account connected at the SyncSuite platform level. The CRM only
 *  reads — the user adds/removes connections in SyncSuite chrome's
 *  Email services page (app.syncsuite.co/<ws>/services?open=email). */
interface MailAccount {
  id: string;
  email: string;
  display_name?: string | null;
  /** gmail | outlook | imap | smtp_only | sendgrid */
  provider: string;
  /** connected | disconnected | error */
  status: string;
  is_default?: boolean;
  /** Surfaced in a tooltip when the account is in error state. */
  last_error?: string | null;
}

interface Thread {
  id: string;
  subject: string;
  snippet: string | null;
  contact_id: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_avatar: string | null;
  participants: { name?: string; email: string; role: string }[];
  message_count: number;
  unread_count: number;
  is_starred: boolean;
  is_important: boolean;
  has_attachments: boolean;
  last_message_at: string | null;
  last_message_preview: string | null;
  last_from_name: string | null;
  conversation_id: string | null;
  account_id: string | null;
  trashed_at: string | null;
  snoozed_until: string | null;
  label_ids: string[];
  created_at: string;
}

interface DraftPayload {
  thread_id: string;
  message_id: string;
  subject: string;
  body: string | null;
  html_body: string | null;
  to_addresses: { email: string; name?: string }[];
  cc_addresses: { email: string; name?: string }[];
  contact_id: string | null;
}

interface EmailMessage {
  id: string;
  thread_id: string | null;
  conversation_id: string;
  contact_id: string | null;
  direction: "inbound" | "outbound";
  status: string;
  body: string | null;
  html_body: string | null;
  subject: string | null;
  snippet: string | null;
  from_address: string | null;
  from_name: string | null;
  to_addresses: { name?: string; email: string }[];
  cc_addresses: { name?: string; email: string }[];
  attachments: unknown[];
  is_starred: boolean;
  is_read: boolean;
  campaign_id: string | null;
  sent_at: string | null;
  created_at: string;
}

interface SmartReply { label: string; text: string; html: string }

// ─── Icon resolver for system labels ───────────────────────────────────────
const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  Inbox: InboxIcon, Star, Clock, Tag, Send, FileEdit,
  OctagonAlert, Trash2, Mail: MailIcon,
};

function labelIcon(label: Label) {
  if (label.icon && ICON_MAP[label.icon]) return ICON_MAP[label.icon];
  return TagIcon;
}

const LABEL_DOT_COLORS: Record<string, string> = {
  zinc: "bg-zinc-500",
  indigo: "bg-rose-500",
  blue: "bg-sky-500",
  emerald: "bg-emerald-500",
  amber: "bg-amber-500",
  rose: "bg-rose-500",
  violet: "bg-rose-500",
  sky: "bg-sky-500",
  teal: "bg-teal-500",
};

// ─── Page ──────────────────────────────────────────────────────────────────

export function MailPage() {
  const toast = useToast();
  const { threadId: routeThreadId } = useParams<{ threadId?: string }>();

  const [labels, setLabels] = useState<Label[]>([]);
  const [labelsLoaded, setLabelsLoaded] = useState(false);
  /** Connected mail accounts pulled from the SyncSuite platform via the
   *  backend `/api/email/accounts` proxy. Empty array means the workspace
   *  hasn't connected anything yet — the rail just renders "All Mail". */
  const [accounts, setAccounts] = useState<MailAccount[]>([]);
  /** Active account id for the "Inbox of [account]" view. null = All Mail
   *  (no account filter applied). */
  const [activeAccountId, setActiveAccountId] = useState<string | null>(null);
  const [activeLabel, setActiveLabel] = useState<string>("Inbox");
  const [threads, setThreads] = useState<Thread[]>([]);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(routeThreadId ?? null);
  const [activeThread, setActiveThread] = useState<Thread | null>(null);
  const [messages, setMessages] = useState<EmailMessage[]>([]);
  const [threadLoading, setThreadLoading] = useState(false);
  /** Thread IDs whose Trash request is currently in flight — used to show a
   *  spinner in both the reader header and the per-row trash chip. A Set
   *  (rather than a single bool) handles the case where the user clicks
   *  delete on multiple rows in quick succession. */
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [smartReplies, setSmartReplies] = useState<SmartReply[]>([]);
  const [smartRepliesLoading, setSmartRepliesLoading] = useState(false);
  /** When set, the compose modal opens in edit-draft mode. The draft's
   *  thread id is stored here; the modal fetches the draft body via
   *  GET /email/drafts/:thread_id and pre-fills its fields. */
  const [editingDraftId, setEditingDraftId] = useState<string | null>(null);
  const [composeOpen, setComposeOpen] = useState(false);
  /** True while the inline "create new label" form in the labels rail is open. */
  const [creatingLabel, setCreatingLabel] = useState(false);
  /** When set, render the right-click context menu at the captured cursor
   *  position, scoped to the target thread. */
  const [contextMenu, setContextMenu] = useState<{ thread: Thread; x: number; y: number } | null>(null);
  const [replyMode, setReplyMode] = useState<"idle" | "reply">("idle");
  const [replyHtml, setReplyHtml] = useState("");
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 200);
    return () => clearTimeout(t);
  }, [q]);

  // Initial labels load
  const loadLabels = async () => {
    try {
      const data = await api.get<{ labels: Label[] }>("/email/labels");
      setLabels(data.labels);
    } catch (err) {
      console.error(err);
    } finally {
      setLabelsLoaded(true);
    }
  };

  // Connected mail accounts (Gmail/Outlook/IMAP/SMTP — all sources). Fetched
  // once on mount; the backend proxies to the SyncSuite platform's services
  // API. Empty array is a normal state — workspaces with no connections
  // still get a working "All Mail" view of the workspace catch-all.
  useEffect(() => {
    api.get<{ accounts: MailAccount[] }>("/email/accounts")
      .then((d) => setAccounts(d.accounts ?? []))
      .catch(() => setAccounts([]));
  }, []);
  useEffect(() => { void loadLabels(); }, []);

  // Threads load on label / search change
  const loadThreads = async () => {
    setLoading(true);
    try {
      const data = await api.get<{ threads: Thread[] }>("/email/threads", {
        label: activeLabel,
        q: debouncedQ || undefined,
        account_id: activeAccountId ?? undefined,
        limit: 100,
      });
      setThreads(data.threads);
      if (!activeThreadId && data.threads.length > 0 && !routeThreadId) {
        setActiveThreadId(data.threads[0]!.id);
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to load threads");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void loadThreads(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [activeLabel, debouncedQ, activeAccountId]);

  // Active thread detail load — race-safe + immediate state clear so a
  // throttled network doesn't leave the previous thread on screen for 2s.
  // Manual refresh (after sending a reply) goes through reloadActiveThread.
  const reloadActiveThread = async () => {
    if (!activeThreadId) return;
    try {
      const data = await api.get<{ thread: Thread; messages: EmailMessage[] }>(`/email/threads/${activeThreadId}`);
      setActiveThread(data.thread);
      setMessages(data.messages);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to load thread");
    }
  };

  useEffect(() => {
    if (!activeThreadId) {
      setActiveThread(null);
      setMessages([]);
      setSmartReplies([]);
      setThreadLoading(false);
      return;
    }
    // Synchronously clear stale content so the old thread isn't visible
    // while the network request is in flight.
    setActiveThread(null);
    setMessages([]);
    setSmartReplies([]);
    setReplyMode("idle");
    setReplyHtml("");
    setReplyText("");
    setThreadLoading(true);

    // Cancellation: when the user clicks a different thread before this
    // request finishes, ignore the in-flight result so it doesn't overwrite
    // the newer fetch.
    let cancelled = false;
    api.get<{ thread: Thread; messages: EmailMessage[] }>(`/email/threads/${activeThreadId}`)
      .then((d) => {
        if (cancelled) return;
        setActiveThread(d.thread);
        setMessages(d.messages);
        // Reflect read-state in list + refresh sidebar unread badges
        setThreads((prev) => prev.map((t) => t.id === activeThreadId ? { ...t, unread_count: 0 } : t));
        void loadLabels();
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(err instanceof ApiError ? err.message : "Failed to load thread");
      })
      .finally(() => {
        if (!cancelled) setThreadLoading(false);
      });

    return () => { cancelled = true; };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [activeThreadId]);

  // Lazy-fetch smart replies the first time the user clicks Reply. Same
  // cancellation pattern so a stale fetch from a previous thread doesn't
  // populate this thread's composer.
  useEffect(() => {
    if (replyMode !== "reply" || !activeThreadId || smartReplies.length > 0) return;
    setSmartRepliesLoading(true);
    let cancelled = false;
    api.get<{ suggestions: SmartReply[] }>(`/email/threads/${activeThreadId}/smart-replies`)
      .then((d) => { if (!cancelled) setSmartReplies(d.suggestions); })
      .catch(() => { if (!cancelled) setSmartReplies([]); })
      .finally(() => { if (!cancelled) setSmartRepliesLoading(false); });
    return () => { cancelled = true; };
  }, [replyMode, activeThreadId, smartReplies.length]);

  /** Snooze a thread until the given ISO timestamp, or wake it now (null). */
  const onSnooze = async (thread: Thread, untilIso: string | null) => {
    setThreads((prev) => {
      // If snoozing while viewing Inbox, the thread vanishes; if waking
      // while viewing Snoozed, same thing.
      const hidden = (untilIso && activeLabel === "Inbox") || (!untilIso && activeLabel === "Snoozed");
      if (hidden) return prev.filter((t) => t.id !== thread.id);
      return prev.map((t) => t.id === thread.id ? { ...t, snoozed_until: untilIso } : t);
    });
    if (activeThread?.id === thread.id) {
      setActiveThread({ ...activeThread, snoozed_until: untilIso });
    }
    try {
      await api.patch(`/email/threads/${thread.id}`, { snooze_until: untilIso });
      void loadLabels();
      toast.success(untilIso ? "Snoozed" : "Unsnoozed");
    } catch {
      toast.error("Failed to snooze");
      void reloadActiveThread();
    }
  };

  /** Toggle a (custom) label on a thread. Used by the right-click menu's
   *  "Apply labels" submenu. */
  const onToggleLabel = async (thread: Thread, labelId: string) => {
    const has = thread.label_ids.includes(labelId);
    // Optimistic update on the list row + active thread
    setThreads((prev) => prev.map((t) => t.id === thread.id
      ? { ...t, label_ids: has ? t.label_ids.filter((id) => id !== labelId) : [...t.label_ids, labelId] }
      : t));
    if (activeThread?.id === thread.id) {
      setActiveThread({
        ...activeThread,
        label_ids: has ? activeThread.label_ids.filter((id) => id !== labelId) : [...activeThread.label_ids, labelId],
      });
    }
    try {
      await api.patch(`/email/threads/${thread.id}`, has ? { remove_labels: [labelId] } : { add_labels: [labelId] });
      void loadLabels();
    } catch {
      toast.error("Failed to update label");
      void reloadActiveThread();
    }
  };

  /** Move a thread INTO Spam from any other view. */
  const onMarkSpam = async (thread: Thread) => {
    const data = await api.get<{ labels: Label[] }>("/email/labels");
    const spam = data.labels.find((l) => l.name === "Spam");
    const inbox = data.labels.find((l) => l.name === "Inbox");
    if (!spam) { toast.error("Spam label missing"); return; }
    try {
      await api.patch(`/email/threads/${thread.id}`, {
        add_labels: [spam.id],
        remove_labels: inbox ? [inbox.id] : [],
      });
      setThreads((prev) => prev.filter((t) => t.id !== thread.id));
      if (activeThreadId === thread.id) setActiveThreadId(null);
      toast.success("Marked as spam");
      void loadLabels();
    } catch {
      toast.error("Failed to update");
    }
  };

  /** Move a thread out of Spam back into Inbox. */
  const onNotSpam = async (thread: Thread) => {
    const labelsRows = await api.get<{ labels: Label[] }>("/email/labels");
    const spam = labelsRows.labels.find((l) => l.name === "Spam");
    const inbox = labelsRows.labels.find((l) => l.name === "Inbox");
    if (!spam || !inbox) { toast.error("Couldn't resolve system labels"); return; }
    try {
      await api.patch(`/email/threads/${thread.id}`, {
        remove_labels: [spam.id],
        add_labels: [inbox.id],
      });
      setThreads((prev) => prev.filter((t) => t.id !== thread.id));
      if (activeThreadId === thread.id) setActiveThreadId(null);
      toast.success("Moved to Inbox");
      void loadLabels();
    } catch {
      toast.error("Failed to update");
    }
  };

  /** Restore a trashed thread (clears trashed_at). */
  const onRestore = async (thread: Thread) => {
    try {
      await api.patch(`/email/threads/${thread.id}`, { trashed: false });
      setThreads((prev) => prev.filter((t) => t.id !== thread.id));
      if (activeThreadId === thread.id) setActiveThreadId(null);
      toast.success("Restored");
      void loadLabels();
    } catch {
      toast.error("Failed to restore");
    }
  };

  /** Hard-delete a thread. Used by Spam + Trash views' "Delete forever". */
  const onDeleteForever = async (thread: Thread) => {
    if (!confirm(`Permanently delete "${thread.subject}"? This can't be undone.`)) return;
    if (deletingIds.has(thread.id)) return;
    setDeletingIds((prev) => new Set(prev).add(thread.id));
    try {
      await api.delete(`/email/threads/${thread.id}`);
      setThreads((prev) => prev.filter((t) => t.id !== thread.id));
      if (activeThreadId === thread.id) setActiveThreadId(null);
      toast.success("Deleted permanently");
      void loadLabels();
    } catch {
      toast.error("Failed to delete");
    } finally {
      setDeletingIds((prev) => {
        if (!prev.has(thread.id)) return prev;
        const next = new Set(prev);
        next.delete(thread.id);
        return next;
      });
    }
  };

  const onToggleStar = async (thread: Thread) => {
    const next = !thread.is_starred;
    setThreads((prev) => prev.map((t) => t.id === thread.id ? { ...t, is_starred: next } : t));
    if (activeThread?.id === thread.id) setActiveThread({ ...activeThread, is_starred: next });
    try {
      await api.patch(`/email/threads/${thread.id}`, { is_starred: next });
      void loadLabels();
    } catch {
      toast.error("Failed to update");
      void reloadActiveThread();
    }
  };

  const onToggleRead = async (thread: Thread) => {
    const next = thread.unread_count > 0 ? true : false; // true = mark read, false = mark unread
    // Optimistic update: in the list set unread_count to 0 or 1; if it's the
    // active thread, mirror the change.
    setThreads((prev) => prev.map((t) =>
      t.id === thread.id ? { ...t, unread_count: next ? 0 : Math.max(t.unread_count, 1) } : t
    ));
    if (activeThread?.id === thread.id) {
      setActiveThread({ ...activeThread, unread_count: next ? 0 : Math.max(activeThread.unread_count, 1) });
    }
    try {
      await api.patch(`/email/threads/${thread.id}`, { is_read: next });
      void loadLabels(); // unread badges in the sidebar need to refresh
    } catch {
      toast.error("Failed to update");
      void loadThreads();
    }
  };

  const onTrash = async (thread: Thread) => {
    // Guard against double-click while a delete is already in flight for
    // this thread — the visible spinner already telegraphs that the action
    // is happening; firing it again is just wasted work.
    if (deletingIds.has(thread.id)) return;
    setDeletingIds((prev) => new Set(prev).add(thread.id));
    try {
      await api.patch(`/email/threads/${thread.id}`, { trashed: true });
      setThreads((prev) => prev.filter((t) => t.id !== thread.id));
      if (activeThreadId === thread.id) setActiveThreadId(null);
      toast.success("Moved to Trash");
      void loadLabels();
    } catch {
      toast.error("Failed to delete");
    } finally {
      setDeletingIds((prev) => {
        if (!prev.has(thread.id)) return prev;
        const next = new Set(prev);
        next.delete(thread.id);
        return next;
      });
    }
  };

  const onSendReply = async () => {
    if (!activeThreadId || !replyText.trim()) return;
    setSending(true);
    try {
      await api.post(`/email/threads/${activeThreadId}/reply`, {
        text: replyText,
        html: replyHtml,
      });
      setReplyHtml("");
      setReplyText("");
      setReplyMode("idle");
      setSmartReplies([]);
      await reloadActiveThread();
      toast.success("Reply sent");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const useSuggestion = (s: SmartReply) => {
    setReplyHtml(s.html);
    setReplyText(s.text);
    setReplyMode("reply");
  };

  return (
    <div className="flex h-full bg-zinc-950">
      {/* ─── Left: Labels rail ───────────────────────────────────────── */}
      <aside className="hidden md:flex w-56 lg:w-60 shrink-0 flex-col border-r border-zinc-900 bg-zinc-950/60 overflow-y-auto">
        {/* Account switcher — only renders if the workspace has connected
            at least one personal mail account at the SyncSuite layer.
            Otherwise the user just sees "All Mail" + their labels, same
            as before (the workspace catch-all is the only source). */}
        {accounts.length > 0 && (
          <AccountSwitcher
            accounts={accounts}
            activeAccountId={activeAccountId}
            onSelect={(id) => {
              setActiveAccountId(id);
              setActiveThreadId(null);
            }}
          />
        )}
        <div className="p-3">
          <Button
            onClick={() => setComposeOpen(true)}
            className="w-full justify-center gap-2 bg-linear-to-br from-rose-500 to-rose-600 hover:from-rose-400 hover:to-rose-500 border-0 shadow-lg shadow-rose-500/20"
          >
            <Plus className="h-4 w-4" /> Compose
          </Button>
        </div>
        {!labelsLoaded ? (
          // Initial-load skeleton so the rail isn't blank for the first
          // ~few hundred ms (especially on throttled networks).
          <div className="px-2 pb-3 space-y-1.5">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 px-3 py-1.5">
                <Skeleton className="h-4 w-4 rounded" />
                <Skeleton className="h-3 flex-1" />
              </div>
            ))}
          </div>
        ) : (
          <>
            <nav className="px-2 pb-3 space-y-0.5">
              {labels.filter((l) => l.is_system).map((label) => {
                const Icon = labelIcon(label);
                const isActive = activeLabel === label.name;
                return (
                  <button
                    key={label.id}
                    onClick={() => { setActiveLabel(label.name); setActiveThreadId(null); }}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-lg px-3 py-1.5 text-sm transition-colors",
                      isActive
                        ? "bg-rose-500/15 text-rose-200 font-medium"
                        : "text-zinc-400 hover:bg-zinc-900 hover:text-zinc-100"
                    )}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="flex-1 text-left truncate">{label.name}</span>
                    {label.unread_count > 0 && (
                      <span className="text-[10px] font-semibold text-rose-300">{label.unread_count}</span>
                    )}
                  </button>
                );
              })}
            </nav>
            <div className="px-3 pt-2 flex items-center gap-1">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-600 px-1 mb-1 flex-1">Labels</p>
              <Tooltip content="Create new label" side="right">
                <button
                  onClick={() => setCreatingLabel(true)}
                  className="h-5 w-5 grid place-items-center rounded text-zinc-500 hover:text-zinc-100 hover:bg-zinc-800 mb-1"
                  aria-label="Create new label"
                >
                  <Plus className="h-3 w-3" />
                </button>
              </Tooltip>
            </div>
            {creatingLabel && (
              <CreateLabelForm
                onSubmit={async (name, color) => {
                  try {
                    await api.post("/email/labels", { name, color });
                    setCreatingLabel(false);
                    void loadLabels();
                    toast.success(`Label "${name}" created`);
                  } catch (err) {
                    toast.error(err instanceof ApiError ? err.message : "Failed to create label");
                  }
                }}
                onCancel={() => setCreatingLabel(false)}
              />
            )}
            <nav className="px-2 pb-3 space-y-0.5">
              {labels.filter((l) => !l.is_system).map((label) => {
                const isActive = activeLabel === label.name;
                return (
                  <button
                    key={label.id}
                    onClick={() => { setActiveLabel(label.name); setActiveThreadId(null); }}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-lg px-3 py-1.5 text-sm transition-colors",
                      isActive
                        ? "bg-zinc-800/70 text-zinc-100"
                        : "text-zinc-400 hover:bg-zinc-900 hover:text-zinc-100"
                    )}
                  >
                    <span className={cn("h-2 w-2 rounded-full shrink-0", LABEL_DOT_COLORS[label.color] ?? "bg-zinc-500")} />
                    <span className="flex-1 text-left truncate">{label.name}</span>
                    {label.thread_count > 0 && (
                      <span className="text-[10px] text-zinc-500">{label.thread_count}</span>
                    )}
                  </button>
                );
              })}
            </nav>
          </>
        )}
        <div className="mt-auto p-3 border-t border-zinc-900">
          <Link
            to="/campaigns"
            className="flex items-center gap-2 text-xs text-zinc-400 hover:text-zinc-100 transition-colors"
          >
            <Megaphone className="h-3.5 w-3.5" />
            <span>Campaigns</span>
            <ExternalLink className="h-3 w-3 ml-auto opacity-60" />
          </Link>
        </div>
      </aside>

      {/* ─── Middle: Thread list ────────────────────────────────────── */}
      <div className={cn(
        "flex flex-col border-r border-zinc-900 overflow-hidden",
        activeThreadId ? "hidden lg:flex w-[360px] xl:w-[400px] shrink-0" : "flex-1"
      )}>
        <div className="px-3 py-2 border-b border-zinc-900 flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={`Search ${activeLabel}…`}
              className="w-full pl-7 pr-2 h-8 rounded-lg bg-zinc-900 border border-zinc-800 text-xs text-zinc-100 placeholder:text-zinc-500 focus:border-rose-500/50 focus:outline-none"
            />
          </div>
          <Tooltip content="Refresh">
            <button
              onClick={() => { void loadThreads(); }}
              className="h-8 w-8 grid place-items-center text-zinc-500 hover:text-zinc-100 hover:bg-zinc-900 rounded-lg"
              aria-label="Refresh"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
          </Tooltip>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-3 space-y-2">
              {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-16" />)}
            </div>
          ) : threads.length === 0 ? (
            <EmptyState
              icon={MailIcon}
              title="No messages"
              description={q ? "Try a different search." : `${activeLabel} is empty.`}
            />
          ) : (
            <ul className="divide-y divide-zinc-900">
              {threads.map((t) => (
                <ThreadListItem
                  key={t.id}
                  thread={t}
                  isActive={t.id === activeThreadId}
                  isDeleting={deletingIds.has(t.id)}
                  onClick={() => {
                    // From the Drafts view, clicking opens the compose
                    // modal in edit mode (Gmail behavior). Everywhere else,
                    // open the regular reader.
                    if (activeLabel === "Drafts") {
                      setEditingDraftId(t.id);
                      setComposeOpen(true);
                    } else {
                      setActiveThreadId(t.id);
                    }
                  }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setContextMenu({ thread: t, x: e.clientX, y: e.clientY });
                  }}
                  onToggleStar={() => onToggleStar(t)}
                  onToggleRead={() => onToggleRead(t)}
                  onTrash={() => onTrash(t)}
                  labelsById={Object.fromEntries(labels.map((l) => [l.id, l]))}
                />
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* ─── Right: Reader (fills the rest — no separate AI panel) ─────── */}
      {!activeThreadId ? (
        <div className="hidden lg:flex flex-1 items-center justify-center bg-zinc-950">
          <div className="text-center max-w-sm px-6">
            <div className="mx-auto mb-4 h-14 w-14 rounded-2xl bg-zinc-900 grid place-items-center">
              <MailIcon className="h-6 w-6 text-zinc-600" />
            </div>
            <h3 className="text-base font-semibold text-zinc-100">Select a conversation</h3>
            <p className="mt-1 text-sm text-zinc-500">Pick a thread from the list to read and reply.</p>
          </div>
        </div>
      ) : (
        <div className="flex flex-1 flex-col overflow-hidden">
          {/* Header renders instantly from the clicked list row while the
              detail GET is in flight — subject/contact/star/etc. are all
              already in the row data, so the user sees something within
              one frame of clicking, not after the network round-trip. */}
          <ThreadReaderHeader
            thread={activeThread ?? threads.find((t) => t.id === activeThreadId) ?? null}
            activeLabel={activeLabel}
            onBack={() => setActiveThreadId(null)}
            onToggleStar={() => activeThread && onToggleStar(activeThread)}
            onToggleRead={() => activeThread && onToggleRead(activeThread)}
            onMarkSpam={() => activeThread && onMarkSpam(activeThread)}
            onTrash={() => activeThread && onTrash(activeThread)}
            onNotSpam={() => activeThread && onNotSpam(activeThread)}
            onRestore={() => activeThread && onRestore(activeThread)}
            onDeleteForever={() => activeThread && onDeleteForever(activeThread)}
            onSnooze={(iso) => activeThread && onSnooze(activeThread, iso)}
            disabled={!activeThread}
            isDeleting={activeThreadId ? deletingIds.has(activeThreadId) : false}
          />

          {/* Scrollable reader area */}
          <div className="flex-1 overflow-y-auto bg-zinc-950">
            <div className="mx-auto max-w-3xl px-4 sm:px-6 py-5 space-y-3">
              {threadLoading || !activeThread ? (
                <MessagesSkeleton />
              ) : (
                messages.map((m, i) => (
                  <MessageCard
                    key={m.id}
                    message={m}
                    isLast={i === messages.length - 1}
                  />
                ))
              )}

              {/* Reply area: hide entirely while the thread is loading so
                  it doesn't appear before the messages do. */}
              {!threadLoading && activeThread && (
              <div className="mt-4">
                {replyMode === "idle" ? (
                  <button
                    onClick={() => setReplyMode("reply")}
                    className="w-full rounded-xl border border-zinc-800 bg-zinc-900/50 px-4 py-3 text-left text-sm text-zinc-500 hover:text-zinc-300 hover:border-zinc-700 transition-colors flex items-center gap-2"
                  >
                    <Reply className="h-4 w-4" />
                    Reply to {activeThread?.contact_name ?? "thread"}…
                  </button>
                ) : (
                  <div className="space-y-2">
                    {/* Smart-reply chips. The chrome AI handles bespoke
                        drafting — these are quick Gmail-style prefab
                        replies the user can click in one shot. */}
                    {smartRepliesLoading ? (
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-zinc-500 pr-1">
                          <Wand2 className="h-3 w-3" /> Quick replies
                        </span>
                        <Skeleton className="h-7 w-28 rounded-full" />
                        <Skeleton className="h-7 w-24 rounded-full" />
                        <Skeleton className="h-7 w-32 rounded-full" />
                      </div>
                    ) : smartReplies.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-zinc-500 pr-1">
                          <Wand2 className="h-3 w-3" /> Quick replies
                        </span>
                        {smartReplies.map((s, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => useSuggestion(s)}
                            className="inline-flex items-center gap-1.5 rounded-full border border-zinc-800 bg-zinc-900/50 hover:bg-rose-500/15 hover:border-rose-500/40 hover:text-rose-200 px-3 py-1 text-xs text-zinc-300 transition-colors"
                          >
                            {s.label}
                          </button>
                        ))}
                      </div>
                    ) : null}

                    <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 overflow-hidden">
                      <div className="px-3 py-2 border-b border-zinc-800 flex items-center gap-2">
                        <Reply className="h-3.5 w-3.5 text-rose-400" />
                        <span className="text-xs text-zinc-400">
                          To: <span className="text-zinc-200">{activeThread?.contact_email ?? "—"}</span>
                        </span>
                        <button
                          onClick={() => { setReplyMode("idle"); setReplyHtml(""); setReplyText(""); }}
                          className="ml-auto text-zinc-500 hover:text-zinc-200"
                        ><X className="h-3.5 w-3.5" /></button>
                      </div>
                      <RichEmailEditor
                        value={replyHtml}
                        onChange={(html, text) => { setReplyHtml(html); setReplyText(text); }}
                        placeholder="Write your reply…"
                        autofocus
                        minHeight="11rem"
                        className="rounded-none border-0 border-t-0"
                      />
                      <div className="px-3 py-2 border-t border-zinc-800 flex items-center gap-2 bg-zinc-950/60">
                        <Button
                          variant="primary"
                          onClick={() => void onSendReply()}
                          disabled={!replyText.trim() || sending}
                          className="bg-linear-to-br from-rose-500 to-rose-600 hover:from-rose-400 hover:to-rose-500 border-0"
                        >
                          <Send className="h-3.5 w-3.5 mr-1.5" />
                          {sending ? "Sending…" : "Send"}
                        </Button>
                        <span className="text-[10px] text-zinc-600 ml-auto">
                          Send goes through SyncSuite Services → your ESP
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Right-click context menu on thread rows */}
      {contextMenu && (
        <ThreadContextMenu
          thread={contextMenu.thread}
          activeLabel={activeLabel}
          x={contextMenu.x}
          y={contextMenu.y}
          customLabels={labels.filter((l) => !l.is_system)}
          onClose={() => setContextMenu(null)}
          onOpen={() => {
            if (activeLabel === "Drafts") {
              setEditingDraftId(contextMenu.thread.id);
              setComposeOpen(true);
            } else {
              setActiveThreadId(contextMenu.thread.id);
            }
            setContextMenu(null);
          }}
          onToggleStar={() => { void onToggleStar(contextMenu.thread); setContextMenu(null); }}
          onToggleRead={() => { void onToggleRead(contextMenu.thread); setContextMenu(null); }}
          onToggleLabel={(lid) => { void onToggleLabel(contextMenu.thread, lid); /* keep menu open so user can toggle multiple */ }}
          onSnooze={(iso) => { void onSnooze(contextMenu.thread, iso); setContextMenu(null); }}
          onMarkSpam={() => { void onMarkSpam(contextMenu.thread); setContextMenu(null); }}
          onNotSpam={() => { void onNotSpam(contextMenu.thread); setContextMenu(null); }}
          onTrash={() => { void onTrash(contextMenu.thread); setContextMenu(null); }}
          onRestore={() => { void onRestore(contextMenu.thread); setContextMenu(null); }}
          onDeleteForever={() => { void onDeleteForever(contextMenu.thread); setContextMenu(null); }}
          onCreateLabel={() => { setContextMenu(null); setCreatingLabel(true); }}
        />
      )}

      {/* Compose modal — opens fresh for new emails, or in edit-mode when
          editingDraftId is set (clicked a row in the Drafts label). */}
      {composeOpen && (
        <ComposeModal
          editingDraftId={editingDraftId}
          onClose={() => { setComposeOpen(false); setEditingDraftId(null); }}
          onSent={() => {
            setComposeOpen(false);
            setEditingDraftId(null);
            void loadThreads();
            void loadLabels();
            toast.success("Email sent");
          }}
          onDraftSaved={() => {
            setComposeOpen(false);
            setEditingDraftId(null);
            void loadThreads();
            void loadLabels();
            toast.success("Draft saved");
          }}
          onDraftDiscarded={() => {
            setComposeOpen(false);
            setEditingDraftId(null);
            void loadThreads();
            void loadLabels();
            toast.success("Draft discarded");
          }}
        />
      )}
    </div>
  );
}

// ─── Snooze popover ─────────────────────────────────────────────────────────
// Gmail-style presets (Later today, Tomorrow, Next week, Custom). Custom
// opens a datetime input. The selected ISO string is reported via onPick;
// the caller patches the thread.

function SnoozePopover({
  isSnoozed,
  onPick,
  onClose,
}: {
  isSnoozed: boolean;
  onPick: (untilIso: string | null) => void;
  onClose: () => void;
}) {
  const [customOpen, setCustomOpen] = useState(false);
  const [customValue, setCustomValue] = useState(() => {
    const d = new Date(Date.now() + 60 * 60 * 1000);
    // datetime-local input wants YYYY-MM-DDTHH:mm in local time
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  });

  // Dismiss on outside-click / Esc — same pattern as the context menu.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && !target.closest("[data-snooze-popover]")) onClose();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [onClose]);

  // Preset times — generated relative to "now" each render so the labels
  // are accurate when the popover opens at 10am vs 8pm.
  const presets = useMemo(() => buildSnoozePresets(), []);

  return (
    <div
      data-snooze-popover
      className="absolute right-0 top-full mt-1 z-50 rounded-xl border border-zinc-800 bg-zinc-950 shadow-2xl shadow-shade/60 p-1 min-w-60 animate-fade-in"
    >
      <p className="px-2 pt-1 pb-1 text-[10px] uppercase tracking-wider text-zinc-500">Snooze until</p>
      {presets.map((p) => (
        <button
          key={p.label}
          type="button"
          onClick={() => onPick(p.iso)}
          className="w-full flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-xs text-zinc-200 hover:bg-zinc-900"
        >
          <span className="flex-1 text-left">{p.label}</span>
          <span className="text-[10px] text-zinc-500">{p.subLabel}</span>
        </button>
      ))}
      <Divider />
      <button
        type="button"
        onClick={() => setCustomOpen((v) => !v)}
        className="w-full flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-xs text-zinc-200 hover:bg-zinc-900"
      >
        <Clock className="h-3.5 w-3.5 text-zinc-500" />
        <span className="flex-1 text-left">Pick a date & time</span>
        <ChevronRight className={cn("h-3 w-3 text-zinc-500 transition-transform", customOpen && "rotate-90")} />
      </button>
      {customOpen && (
        <div className="px-2 py-2 space-y-2">
          <input
            type="datetime-local"
            value={customValue}
            onChange={(e) => setCustomValue(e.target.value)}
            className="w-full h-8 px-2 rounded-md bg-zinc-900 border border-zinc-800 text-xs text-zinc-100 focus:border-rose-500/50 focus:outline-none"
          />
          <button
            type="button"
            onClick={() => {
              if (!customValue) return;
              const iso = new Date(customValue).toISOString();
              onPick(iso);
            }}
            className="w-full h-7 rounded-md bg-rose-500 hover:bg-rose-400 text-[11px] font-medium text-white"
          >
            Snooze until this time
          </button>
        </div>
      )}
      {isSnoozed && (
        <>
          <Divider />
          <button
            type="button"
            onClick={() => onPick(null)}
            className="w-full flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-xs text-emerald-300 hover:bg-zinc-900"
          >
            <ArchiveRestore className="h-3.5 w-3.5" />
            <span className="flex-1 text-left">Wake up now</span>
          </button>
        </>
      )}
    </div>
  );
}

function buildSnoozePresets(): { label: string; subLabel: string; iso: string }[] {
  const now = new Date();
  const presets: { label: string; subLabel: string; iso: string }[] = [];

  // Later today = 3 hours from now, but only if that's still today
  const inThreeHours = new Date(now.getTime() + 3 * 60 * 60 * 1000);
  if (inThreeHours.getDate() === now.getDate()) {
    presets.push({
      label: "Later today",
      subLabel: inThreeHours.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }),
      iso: inThreeHours.toISOString(),
    });
  }

  // Tomorrow 8am
  const tomorrow = new Date(now); tomorrow.setDate(now.getDate() + 1); tomorrow.setHours(8, 0, 0, 0);
  presets.push({
    label: "Tomorrow morning",
    subLabel: tomorrow.toLocaleDateString(undefined, { weekday: "short" }) + " 8:00 AM",
    iso: tomorrow.toISOString(),
  });

  // This weekend (Saturday 8am)
  const sat = new Date(now); sat.setDate(now.getDate() + ((6 - now.getDay() + 7) % 7 || 7)); sat.setHours(8, 0, 0, 0);
  if (sat.getDate() !== tomorrow.getDate()) {
    presets.push({
      label: "This weekend",
      subLabel: "Sat 8:00 AM",
      iso: sat.toISOString(),
    });
  }

  // Next week (next Monday 8am)
  const mon = new Date(now);
  const daysUntilMonday = ((1 - now.getDay() + 7) % 7) || 7;
  mon.setDate(now.getDate() + daysUntilMonday); mon.setHours(8, 0, 0, 0);
  presets.push({
    label: "Next week",
    subLabel: "Mon 8:00 AM",
    iso: mon.toISOString(),
  });

  // Next month (in 30 days, same time)
  const inMonth = new Date(now); inMonth.setDate(now.getDate() + 30);
  presets.push({
    label: "Next month",
    subLabel: inMonth.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    iso: inMonth.toISOString(),
  });

  return presets;
}

// ─── Right-click thread context menu ───────────────────────────────────────
// Portals to the body so it can escape the scroll containers of the
// thread list + reader. Closes on outside-click, on Escape, and after any
// action that takes the thread out of view (move to trash, mark spam,
// open thread). The "Apply labels" submenu deliberately leaves the menu
// open so the user can toggle multiple labels in one shot.

function ThreadContextMenu({
  thread,
  activeLabel,
  x,
  y,
  customLabels,
  onClose,
  onOpen,
  onToggleStar,
  onToggleRead,
  onToggleLabel,
  onSnooze,
  onMarkSpam,
  onNotSpam,
  onTrash,
  onRestore,
  onDeleteForever,
  onCreateLabel,
}: {
  thread: Thread;
  activeLabel: string;
  x: number;
  y: number;
  customLabels: Label[];
  onClose: () => void;
  onOpen: () => void;
  onToggleStar: () => void;
  onToggleRead: () => void;
  onToggleLabel: (labelId: string) => void;
  onSnooze: (untilIso: string | null) => void;
  onMarkSpam: () => void;
  onNotSpam: () => void;
  onTrash: () => void;
  onRestore: () => void;
  onDeleteForever: () => void;
  onCreateLabel: () => void;
}) {
  const [submenu, setSubmenu] = useState<"labels" | "snooze" | null>(null);
  const presets = useMemo(() => buildSnoozePresets(), []);
  const isSnoozed = !!thread.snoozed_until && new Date(thread.snoozed_until) > new Date();

  // Dismiss on Escape or outside-click. Re-runs whenever the menu mounts.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && !target.closest("[data-mail-context-menu]")) onClose();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [onClose]);

  // Clamp position so the menu never overflows the viewport. Menu is
  // ~220px wide × ~variable tall — measure on first render and re-clamp.
  const vw = typeof window !== "undefined" ? window.innerWidth : 1200;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  const menuWidth = 224;
  const menuHeight = 340; // conservative upper bound
  const left = Math.min(x, vw - menuWidth - 8);
  const top = Math.min(y, vh - menuHeight - 8);

  const inSpam = activeLabel === "Spam";
  const inTrash = activeLabel === "Trash";
  const inDrafts = activeLabel === "Drafts";
  const isUnread = thread.unread_count > 0;

  return createPortal(
    <div
      data-mail-context-menu
      className="fixed z-50 rounded-xl border border-zinc-800 bg-zinc-950 shadow-2xl shadow-shade/60 p-1 min-w-56 animate-fade-in"
      style={{ left, top }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <MenuItem icon={MailOpen} label={isUnread ? "Mark as read" : "Mark as unread"} onClick={onToggleRead} />
      <MenuItem
        icon={Star}
        label={thread.is_starred ? "Remove star" : "Add star"}
        iconClass={thread.is_starred ? "text-amber-400 fill-current" : ""}
        onClick={onToggleStar}
      />

      {/* Snooze — submenu of preset times, or "Wake up now" if already snoozed */}
      {!inDrafts && !inSpam && !inTrash && (
        <div
          className="relative"
          onMouseEnter={() => setSubmenu("snooze")}
          onMouseLeave={() => setSubmenu(null)}
        >
          <button
            type="button"
            className={cn(
              "w-full flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-xs hover:bg-zinc-900",
              isSnoozed ? "text-sky-300" : "text-zinc-200"
            )}
          >
            <Clock className={cn("h-3.5 w-3.5", isSnoozed ? "text-sky-300" : "text-zinc-500")} />
            <span className="flex-1 text-left">{isSnoozed ? "Change snooze" : "Snooze"}</span>
            <ChevronRight className="h-3 w-3 text-zinc-500" />
          </button>
          {submenu === "snooze" && (
            <div className="absolute left-full top-0 -mt-1 ml-1 rounded-xl border border-zinc-800 bg-zinc-950 shadow-2xl shadow-shade/60 p-1 min-w-52">
              {presets.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onSnooze(p.iso); }}
                  className="w-full flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-xs text-zinc-200 hover:bg-zinc-900"
                >
                  <span className="flex-1 text-left">{p.label}</span>
                  <span className="text-[10px] text-zinc-500">{p.subLabel}</span>
                </button>
              ))}
              {isSnoozed && (
                <>
                  <Divider />
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onSnooze(null); }}
                    className="w-full flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-xs text-emerald-300 hover:bg-zinc-900"
                  >
                    <ArchiveRestore className="h-3.5 w-3.5" />
                    <span className="flex-1 text-left">Wake up now</span>
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}

      <Divider />

      {/* Apply labels — submenu shows custom labels with checkbox state */}
      {!inDrafts && (
        <div
          className="relative"
          onMouseEnter={() => setSubmenu("labels")}
          onMouseLeave={() => setSubmenu(null)}
        >
          <button
            type="button"
            className="w-full flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-xs text-zinc-200 hover:bg-zinc-900"
          >
            <Tag className="h-3.5 w-3.5 text-zinc-500" />
            <span className="flex-1 text-left">Apply labels</span>
            <ChevronRight className="h-3 w-3 text-zinc-500" />
          </button>
          {submenu === "labels" && (
            <div className="absolute left-full top-0 -mt-1 ml-1 rounded-xl border border-zinc-800 bg-zinc-950 shadow-2xl shadow-shade/60 p-1 min-w-48">
              {customLabels.length === 0 ? (
                <p className="px-2.5 py-2 text-[11px] text-zinc-500">No custom labels yet.</p>
              ) : (
                customLabels.map((label) => {
                  const applied = thread.label_ids.includes(label.id);
                  return (
                    <button
                      key={label.id}
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onToggleLabel(label.id); }}
                      className="w-full flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-xs text-zinc-200 hover:bg-zinc-900"
                    >
                      <span className="h-4 w-4 grid place-items-center">
                        {applied && <Check className="h-3 w-3 text-rose-300" />}
                      </span>
                      <span className={cn("h-2 w-2 rounded-full shrink-0", LABEL_DOT_COLORS[label.color] ?? "bg-zinc-500")} />
                      <span className="flex-1 text-left">{label.name}</span>
                    </button>
                  );
                })
              )}
              <Divider />
              <MenuItem icon={Plus} label="New label…" onClick={onCreateLabel} />
            </div>
          )}
        </div>
      )}

      {!inDrafts && <Divider />}

      {/* Destructive/contextual actions vary by where the user is browsing. */}
      {!inSpam && !inTrash && !inDrafts && (
        <MenuItem icon={ShieldX} label="Mark as spam" onClick={onMarkSpam} className="text-rose-300" />
      )}
      {inSpam && (
        <MenuItem icon={ShieldCheck} label="Not spam — move to Inbox" onClick={onNotSpam} className="text-emerald-300" />
      )}
      {inTrash && (
        <MenuItem icon={ArchiveRestore} label="Restore from Trash" onClick={onRestore} className="text-emerald-300" />
      )}
      {!inSpam && !inTrash && (
        <MenuItem icon={Trash2} label="Move to Trash" onClick={onTrash} className="text-rose-300" />
      )}
      {(inSpam || inTrash) && (
        <MenuItem icon={Trash2} label="Delete forever" onClick={onDeleteForever} className="text-rose-300" />
      )}

      <Divider />

      <MenuItem
        icon={ExternalLink}
        label={inDrafts ? "Open draft" : "Open thread"}
        onClick={onOpen}
      />
    </div>,
    document.body
  );
}

function MenuItem({
  icon: Icon,
  label,
  onClick,
  iconClass,
  className,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
  iconClass?: string;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className={cn(
        "w-full flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-xs text-zinc-200 hover:bg-zinc-900",
        className
      )}
    >
      <Icon className={cn("h-3.5 w-3.5 text-zinc-500", iconClass)} />
      <span className="flex-1 text-left">{label}</span>
    </button>
  );
}

function Divider() {
  return <div className="my-1 h-px bg-zinc-900 mx-1" />;
}

// ─── Account switcher ──────────────────────────────────────────────────────
// Top of the labels rail. Lists "All Mail" + one entry per connected mail
// account (Gmail, Outlook, custom IMAP). Selecting one filters every
// thread query by `account_id`; the labels (Inbox, Sent, etc.) and search
// nest beneath the chosen account.
//
// Adding/removing/reconfiguring an account happens at the SyncSuite
// platform layer (Email services page, outside the CRM). The rail just
// reads what the platform exposes.

function AccountSwitcher({
  accounts,
  activeAccountId,
  onSelect,
}: {
  accounts: MailAccount[];
  activeAccountId: string | null;
  onSelect: (id: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const active = activeAccountId ? accounts.find((a) => a.id === activeAccountId) ?? null : null;

  // Close on outside click — simple pattern, no portal needed since the
  // rail is the highest stacking context here.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!(e.target as HTMLElement | null)?.closest("[data-account-switcher]")) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const statusDot = (s: string) =>
    s === "connected" ? "bg-emerald-400"
    : s === "error" ? "bg-rose-400"
    : "bg-zinc-500";

  return (
    <div data-account-switcher className="relative px-3 pt-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900/40 hover:bg-zinc-900 px-2.5 py-2 text-left transition-colors"
      >
        <span className={cn("h-2 w-2 rounded-full shrink-0", active ? statusDot(active.status) : "bg-zinc-500")} />
        <span className="flex-1 min-w-0">
          <span className="block text-[10px] uppercase tracking-wider text-zinc-500">Mailbox</span>
          <span className="block text-xs text-zinc-100 truncate">
            {active ? (active.display_name ?? active.email) : "All Mail"}
          </span>
        </span>
        <ChevronRight className={cn("h-3 w-3 text-zinc-500 transition-transform shrink-0", open && "rotate-90")} />
      </button>
      {open && (
        <div className="absolute left-3 right-3 top-full mt-1 z-30 rounded-xl border border-zinc-800 bg-zinc-950 shadow-2xl shadow-shade/60 p-1">
          <button
            type="button"
            onClick={() => { onSelect(null); setOpen(false); }}
            className={cn(
              "w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-xs",
              activeAccountId === null ? "bg-rose-500/15 text-rose-200" : "text-zinc-200 hover:bg-zinc-900"
            )}
          >
            <MailIcon className="h-3.5 w-3.5 text-zinc-500" />
            <span className="flex-1 text-left">All Mail</span>
            {activeAccountId === null && <Check className="h-3 w-3 text-rose-300" />}
          </button>
          {accounts.length > 0 && <div className="my-1 h-px bg-zinc-900 mx-1" />}
          {accounts.map((a) => {
            const isActive = activeAccountId === a.id;
            return (
              <button
                key={a.id}
                type="button"
                onClick={() => { onSelect(a.id); setOpen(false); }}
                className={cn(
                  "w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-xs",
                  isActive ? "bg-rose-500/15 text-rose-200" : "text-zinc-200 hover:bg-zinc-900"
                )}
                title={a.last_error ?? undefined}
              >
                <span className={cn("h-2 w-2 rounded-full shrink-0", statusDot(a.status))} />
                <span className="flex-1 min-w-0 text-left">
                  <span className="block truncate">{a.display_name ?? a.email}</span>
                  {a.display_name && (
                    <span className="block text-[10px] text-zinc-500 truncate">{a.email}</span>
                  )}
                </span>
                {isActive && <Check className="h-3 w-3 text-rose-300" />}
              </button>
            );
          })}
          <div className="my-1 h-px bg-zinc-900 mx-1" />
          <p className="px-2 py-1 text-[10px] text-zinc-500 leading-relaxed">
            Connect or manage accounts in your SyncSuite Email settings.
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Create-label inline form ──────────────────────────────────────────────
// Rendered inside the labels rail when the user clicks the "+" next to
// the "Labels" heading. Submits to POST /email/labels and the rail
// reloads on success.

const LABEL_COLOR_OPTIONS = [
  "indigo", "blue", "sky", "teal", "emerald",
  "amber", "rose", "violet", "fuchsia", "zinc",
];

function CreateLabelForm({
  onSubmit,
  onCancel,
}: {
  onSubmit: (name: string, color: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [name, setName] = useState("");
  const [color, setColor] = useState("indigo");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name.trim() || saving) return;
    setSaving(true);
    try { await onSubmit(name.trim(), color); }
    finally { setSaving(false); }
  };

  return (
    <div className="px-2 pb-2">
      <div className="rounded-lg border border-zinc-800 bg-zinc-900/60 p-2 space-y-2">
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { void submit(); }
            else if (e.key === "Escape") { onCancel(); }
          }}
          placeholder="Label name"
          className="w-full h-7 px-2 rounded-md bg-zinc-950 border border-zinc-800 text-xs text-zinc-100 placeholder:text-zinc-500 focus:border-rose-500/50 focus:outline-none"
        />
        <div className="flex flex-wrap gap-1">
          {LABEL_COLOR_OPTIONS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setColor(c)}
              className={cn(
                "h-5 w-5 rounded-full transition-transform",
                LABEL_DOT_COLORS[c] ?? "bg-zinc-500",
                color === c ? "ring-2 ring-zinc-100 ring-offset-1 ring-offset-zinc-900 scale-110" : "hover:scale-110"
              )}
              aria-label={`Color ${c}`}
            />
          ))}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => void submit()}
            disabled={!name.trim() || saving}
            className="flex-1 h-7 px-2 rounded-md bg-rose-500 hover:bg-rose-400 disabled:bg-rose-500/30 disabled:cursor-not-allowed text-[11px] font-medium text-white"
          >
            {saving ? "Saving…" : "Create"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="h-7 px-2 rounded-md bg-zinc-800 hover:bg-zinc-700 text-[11px] text-zinc-300"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Thread list row ───────────────────────────────────────────────────────

function ThreadListItem({
  thread,
  isActive,
  isDeleting,
  onClick,
  onContextMenu,
  onToggleStar,
  onToggleRead,
  onTrash,
  labelsById,
}: {
  thread: Thread;
  isActive: boolean;
  /** True while a Trash request is in flight for this thread — swaps the
   *  trash chip's icon for a spinner so the user gets immediate feedback
   *  on slow networks. */
  isDeleting: boolean;
  onClick: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
  onToggleStar: () => void;
  onToggleRead: () => void;
  onTrash: () => void;
  labelsById: Record<string, Label>;
}) {
  const fromName = thread.last_from_name || thread.contact_name || thread.contact_email || "Unknown";
  const unread = thread.unread_count > 0;
  const customLabels = thread.label_ids
    .map((id) => labelsById[id])
    .filter((l): l is Label => !!l && !l.is_system && l.name !== "Starred");

  // The whole row is a button (for the open-thread click) — but we want
  // additional quick-action buttons inside the row that don't trigger
  // the open. Solve with a div + onClick instead of a button-in-button,
  // and add role="button" + keyboard support for accessibility.
  return (
    <li
      onClick={onClick}
      onContextMenu={onContextMenu}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } }}
      tabIndex={0}
      role="button"
      className={cn(
        "group relative w-full px-3 py-2.5 flex gap-3 text-left transition-colors cursor-pointer outline-none",
        isActive ? "bg-zinc-900/80" : "hover:bg-zinc-900/40 focus-visible:bg-zinc-900/40",
        unread && !isActive ? "bg-zinc-900/20" : ""
      )}
    >
      <Tooltip content={thread.is_starred ? "Unstar" : "Star this thread"} side="right">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggleStar(); }}
          className={cn(
            "shrink-0 mt-0.5 h-5 w-5 grid place-items-center rounded transition-colors",
            thread.is_starred ? "text-amber-400" : "text-zinc-600 hover:text-amber-400"
          )}
          aria-label={thread.is_starred ? "Unstar" : "Star this thread"}
        >
          <Star className={cn("h-3.5 w-3.5", thread.is_starred && "fill-current")} />
        </button>
      </Tooltip>

      <Avatar name={fromName} email={thread.contact_email ?? undefined} src={thread.contact_avatar ?? undefined} size="sm" />

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-0.5">
          <span className={cn(
            "truncate text-xs",
            unread ? "text-zinc-50 font-semibold" : "text-zinc-300"
          )}>{fromName}</span>
          {thread.message_count > 1 && (
            <span className="text-[10px] text-zinc-500 shrink-0">{thread.message_count}</span>
          )}
          {/* Right cluster: hover-revealed action chips REPLACE the timestamp
              on hover, so the row stays the same height and chips don't
              overflow on narrow widths. When a delete is in flight on this
              row, the cluster stays pinned visible so the spinner doesn't
              disappear when the cursor moves off the row. */}
          <span className="ml-auto shrink-0 relative h-5">
            <span className={cn(
              "absolute right-0 top-1/2 -translate-y-1/2 text-[10px] text-zinc-500 transition-opacity",
              isDeleting ? "opacity-0" : "group-hover:opacity-0"
            )}>
              {formatRelative(thread.last_message_at)}
            </span>
            <span className={cn(
              "absolute right-0 top-1/2 -translate-y-1/2 flex items-center gap-0.5 transition-opacity",
              isDeleting ? "opacity-100" : "opacity-0 group-hover:opacity-100"
            )}>
              <RowAction
                title={unread ? "Mark as read" : "Mark as unread"}
                onClick={(e) => { e.stopPropagation(); onToggleRead(); }}
                icon={unread ? MailOpen : MailIcon}
                hoverClasses="hover:text-rose-300"
                disabled={isDeleting}
              />
              <RowAction
                title={isDeleting ? "Deleting…" : "Move to trash"}
                onClick={(e) => { e.stopPropagation(); onTrash(); }}
                icon={Trash2}
                hoverClasses="hover:text-rose-300"
                loading={isDeleting}
                activeColor="text-rose-300"
              />
            </span>
          </span>
        </div>
        <p className={cn(
          "truncate text-xs",
          unread ? "text-zinc-100 font-medium" : "text-zinc-400"
        )}>{thread.subject || "(no subject)"}</p>
        <p className="truncate text-[11px] text-zinc-500 mt-0.5">{thread.snippet ?? thread.last_message_preview}</p>
        {thread.snoozed_until && new Date(thread.snoozed_until) > new Date() && (
          <p className="mt-1 text-[10px] text-sky-300 inline-flex items-center gap-1">
            <Clock className="h-2.5 w-2.5" />
            Snoozed until {new Date(thread.snoozed_until).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
          </p>
        )}
        {customLabels.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {customLabels.map((l) => (
              <Badge key={l.id} color={l.color} size="xs">{l.name}</Badge>
            ))}
          </div>
        )}
      </div>
    </li>
  );
}

/** Tiny icon button used in the thread-list row's hover cluster.
 *  hoverClasses + activeColor must be full Tailwind class strings (e.g.
 *  "hover:text-rose-300", "text-rose-300") so the JIT picks them up at
 *  build time — never compose at runtime. */
function RowAction({
  icon: Icon,
  title,
  onClick,
  hoverClasses,
  loading = false,
  disabled = false,
  activeColor,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  onClick: (e: React.MouseEvent) => void;
  hoverClasses: string;
  loading?: boolean;
  disabled?: boolean;
  activeColor?: string;
}) {
  return (
    <Tooltip content={title} side="top">
      <button
        type="button"
        onClick={(e) => {
          if (loading || disabled) { e.stopPropagation(); return; }
          onClick(e);
        }}
        disabled={loading || disabled}
        aria-label={title}
        className={cn(
          "h-6 w-6 grid place-items-center rounded transition-colors disabled:cursor-not-allowed",
          loading ? (activeColor ?? "text-zinc-300") : cn("text-zinc-500 hover:bg-zinc-800", hoverClasses),
          disabled && !loading && "opacity-40"
        )}
      >
        {loading
          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
          : <Icon className="h-3.5 w-3.5" />}
      </button>
    </Tooltip>
  );
}

// ─── Reader header ─────────────────────────────────────────────────────────

function ThreadReaderHeader({
  thread,
  activeLabel,
  onBack,
  onToggleStar,
  onToggleRead,
  onMarkSpam,
  onTrash,
  onNotSpam,
  onRestore,
  onDeleteForever,
  onSnooze,
  disabled = false,
  isDeleting = false,
}: {
  thread: Thread | null;
  activeLabel: string;
  onBack: () => void;
  onToggleStar: () => void;
  onToggleRead: () => void;
  onMarkSpam: () => void;
  onTrash: () => void;
  onNotSpam: () => void;
  onRestore: () => void;
  onDeleteForever: () => void;
  onSnooze: (untilIso: string | null) => void;
  disabled?: boolean;
  isDeleting?: boolean;
}) {
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  if (!thread) return null;
  const isUnread = thread.unread_count > 0;
  const inSpam = activeLabel === "Spam";
  const inTrash = activeLabel === "Trash";
  const isSnoozed = !!thread.snoozed_until && new Date(thread.snoozed_until) > new Date();

  return (
    <div className="border-b border-zinc-900 px-3 sm:px-5 py-3 flex items-center gap-2 bg-zinc-950">
      <Tooltip content="Back to list">
        <button
          onClick={onBack}
          className="lg:hidden h-8 w-8 grid place-items-center text-zinc-400 hover:text-zinc-100 rounded-lg hover:bg-zinc-900"
          aria-label="Back to list"
        ><ChevronLeft className="h-4 w-4" /></button>
      </Tooltip>
      <div className="min-w-0 flex-1">
        <h2 className="text-sm sm:text-base font-semibold text-zinc-100 truncate">{thread.subject}</h2>
        <div className="flex items-center gap-2 mt-0.5">
          {thread.contact_id && (
            <Link
              to={`/contacts/${thread.contact_id}`}
              state={{ from: `/mail/${thread.id}` }}
              className="text-[11px] text-zinc-500 hover:text-rose-300 inline-flex items-center gap-1 truncate"
            >
              <AtSign className="h-3 w-3" />
              <span className="truncate">{thread.contact_name ?? thread.contact_email}</span>
              <ExternalLink className="h-2.5 w-2.5" />
            </Link>
          )}
          {isSnoozed && thread.snoozed_until && (
            <Tooltip content="Click to wake this thread now">
              <button
                type="button"
                onClick={() => onSnooze(null)}
                className="inline-flex items-center gap-1 text-[11px] text-sky-300 hover:text-sky-200 bg-sky-500/10 hover:bg-sky-500/20 rounded-md px-1.5 py-0.5"
              >
                <Clock className="h-3 w-3" />
                Snoozed until {new Date(thread.snoozed_until).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
              </button>
            </Tooltip>
          )}
        </div>
      </div>
      <div className={cn("flex items-center gap-0.5", disabled && "opacity-40 pointer-events-none")}>
        {/* Spam-specific: Not spam */}
        {inSpam && (
          <Tooltip content="This isn't spam — move back to Inbox">
            <button
              onClick={onNotSpam}
              disabled={disabled}
              className="h-8 px-2.5 inline-flex items-center gap-1.5 rounded-lg text-emerald-300 hover:bg-emerald-500/15 disabled:cursor-not-allowed text-xs font-medium"
            >
              <ShieldCheck className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Not spam</span>
            </button>
          </Tooltip>
        )}

        {/* Trash-specific: Restore */}
        {inTrash && (
          <Tooltip content="Restore from Trash">
            <button
              onClick={onRestore}
              disabled={disabled}
              className="h-8 px-2.5 inline-flex items-center gap-1.5 rounded-lg text-emerald-300 hover:bg-emerald-500/15 disabled:cursor-not-allowed text-xs font-medium"
            >
              <ArchiveRestore className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Restore</span>
            </button>
          </Tooltip>
        )}

        {/* Hide mark-read / star / mark-spam from Spam + Trash — they're
            noisy actions on a thread the user has effectively discarded. */}
        {!inSpam && !inTrash && (
          <>
            <Tooltip content={isUnread ? "Mark as read" : "Mark as unread"}>
              <button
                onClick={onToggleRead}
                disabled={disabled}
                className="h-8 w-8 grid place-items-center rounded-lg hover:bg-zinc-900 text-zinc-500 hover:text-rose-300 disabled:cursor-not-allowed"
                aria-label={isUnread ? "Mark as read" : "Mark as unread"}
              >
                {isUnread ? <MailOpen className="h-4 w-4" /> : <MailIcon className="h-4 w-4" />}
              </button>
            </Tooltip>
            <Tooltip content={thread.is_starred ? "Remove star" : "Star this thread"}>
              <button
                onClick={onToggleStar}
                disabled={disabled}
                className={cn(
                  "h-8 w-8 grid place-items-center rounded-lg hover:bg-zinc-900 disabled:cursor-not-allowed",
                  thread.is_starred ? "text-amber-400" : "text-zinc-500 hover:text-zinc-100"
                )}
                aria-label={thread.is_starred ? "Remove star" : "Star this thread"}
              ><Star className={cn("h-4 w-4", thread.is_starred && "fill-current")} /></button>
            </Tooltip>
            <div className="relative">
              <Tooltip content={isSnoozed ? "Snooze settings" : "Snooze (hide until later)"}>
                <button
                  onClick={() => setSnoozeOpen((v) => !v)}
                  disabled={disabled}
                  className={cn(
                    "h-8 w-8 grid place-items-center rounded-lg hover:bg-zinc-900 disabled:cursor-not-allowed",
                    isSnoozed ? "text-sky-300" : "text-zinc-500 hover:text-sky-300"
                  )}
                  aria-label="Snooze"
                ><Clock className="h-4 w-4" /></button>
              </Tooltip>
              {snoozeOpen && (
                <SnoozePopover
                  isSnoozed={isSnoozed}
                  onPick={(iso) => { setSnoozeOpen(false); onSnooze(iso); }}
                  onClose={() => setSnoozeOpen(false)}
                />
              )}
            </div>
            <Tooltip content="Mark as spam">
              <button
                onClick={onMarkSpam}
                disabled={disabled}
                className="h-8 w-8 grid place-items-center rounded-lg hover:bg-zinc-900 text-zinc-500 hover:text-rose-300 disabled:cursor-not-allowed"
                aria-label="Mark as spam"
              ><ShieldX className="h-4 w-4" /></button>
            </Tooltip>
          </>
        )}

        {/* Final destructive button: from Spam/Trash, this is hard-delete;
            from anywhere else, it's move-to-trash (reversible). */}
        <Tooltip content={isDeleting ? "Deleting…" : (inSpam || inTrash) ? "Delete forever (can't be undone)" : "Move to trash"}>
          <button
            onClick={inSpam || inTrash ? onDeleteForever : onTrash}
            disabled={disabled || isDeleting}
            className={cn(
              "h-8 w-8 grid place-items-center rounded-lg hover:bg-zinc-900 disabled:cursor-not-allowed",
              isDeleting ? "text-rose-300" : "text-zinc-500 hover:text-rose-300"
            )}
            aria-label={isDeleting ? "Deleting…" : (inSpam || inTrash) ? "Delete forever" : "Move to trash"}
          >
            {isDeleting
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <Trash2 className="h-4 w-4" />}
          </button>
        </Tooltip>
      </div>
    </div>
  );
}

/** Skeleton placeholder for the messages area while a thread detail is
 *  loading. Renders 2–3 message-card-shaped blocks so the area has the same
 *  visual weight as the real thing — prevents layout shift when the
 *  messages arrive. */
function MessagesSkeleton() {
  return (
    <>
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
          <div className="flex items-start gap-3">
            <Skeleton className="h-8 w-8 rounded-full" />
            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-2">
                <Skeleton className="h-3 w-32" />
                <Skeleton className="ml-auto h-3 w-14" />
              </div>
              <Skeleton className="h-2.5 w-3/4" />
              <div className="space-y-1.5 pt-2">
                <Skeleton className="h-2.5 w-full" />
                <Skeleton className="h-2.5 w-11/12" />
                <Skeleton className="h-2.5 w-3/5" />
              </div>
            </div>
          </div>
        </div>
      ))}
    </>
  );
}

// ─── Single message card ───────────────────────────────────────────────────

function MessageCard({ message, isLast }: { message: EmailMessage; isLast: boolean }) {
  const [expanded, setExpanded] = useState(isLast);
  const name = message.from_name || message.from_address || "Unknown";

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 overflow-hidden">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full px-4 py-3 flex items-start gap-3 hover:bg-zinc-900/60"
      >
        <Avatar name={name} email={message.from_address ?? undefined} size="sm" />
        <div className="min-w-0 flex-1 text-left">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-zinc-100 truncate">{name}</span>
            {message.direction === "outbound" && (
              <Badge color="emerald" size="xs">Sent</Badge>
            )}
            {message.campaign_id && (
              <Badge color="violet" size="xs">Campaign</Badge>
            )}
            <span className="ml-auto text-[11px] text-zinc-500 shrink-0">{formatRelative(message.created_at)}</span>
          </div>
          <p className="text-[11px] text-zinc-500 truncate">
            to {message.to_addresses.map((a) => a.name ?? a.email).join(", ") || "—"}
          </p>
          {!expanded && (
            <p className="mt-1 text-xs text-zinc-400 truncate">{message.snippet ?? message.body?.slice(0, 120)}</p>
          )}
        </div>
      </button>
      {expanded && (
        <div className="px-4 sm:px-5 pb-4 pt-1">
          {message.html_body ? (
            <div
              className="prose prose-sm max-w-none text-sm text-zinc-200 prose-headings:text-zinc-100 prose-strong:text-zinc-100 prose-a:text-rose-400 prose-blockquote:text-zinc-300 prose-code:text-zinc-200 prose-li:marker:text-zinc-500 [&_a]:text-rose-300 [&_p]:my-2 [&_ul]:list-disc [&_ol]:list-decimal [&_img]:rounded-lg [&_img]:max-w-full"
              dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(message.html_body) }}
            />
          ) : (
            <pre className="text-sm text-zinc-200 whitespace-pre-wrap font-sans leading-relaxed">{message.body}</pre>
          )}
          {message.attachments.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {message.attachments.map((a, i) => {
                const att = a as { name?: string; url?: string };
                return (
                  <a
                    key={i}
                    href={att.url ?? "#"}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-zinc-800 text-xs text-zinc-300 hover:bg-zinc-700"
                  >
                    <Paperclip className="h-3 w-3" />
                    {att.name ?? `Attachment ${i + 1}`}
                  </a>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Compose modal ────────────────────────────────────────────────────────

function ComposeModal({
  editingDraftId,
  onClose,
  onSent,
  onDraftSaved,
  onDraftDiscarded,
}: {
  /** When set, the modal opens in edit-draft mode: prefills from the draft
   *  + the Send button triggers /drafts/:id/send instead of /compose. */
  editingDraftId: string | null;
  onClose: () => void;
  onSent: () => void;
  onDraftSaved: () => void;
  onDraftDiscarded: () => void;
}) {
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  /** When a draft exists in the DB for this compose session — either we
   *  opened editing it, or we Save-Draft'd a brand-new compose and have a
   *  thread id to update on subsequent saves. */
  const [draftThreadId, setDraftThreadId] = useState<string | null>(editingDraftId);
  const [loadingDraft, setLoadingDraft] = useState(!!editingDraftId);
  const toast = useToast();

  // Load existing draft on open if we're editing
  useEffect(() => {
    if (!editingDraftId) return;
    setLoadingDraft(true);
    api.get<{ draft: DraftPayload }>(`/email/drafts/${editingDraftId}`)
      .then((d) => {
        const draft = d.draft;
        setTo((draft.to_addresses ?? []).map((r) => r.email).join(", "));
        setSubject(draft.subject ?? "");
        setHtml(draft.html_body ?? "");
        setText(draft.body ?? "");
        setDraftThreadId(draft.thread_id);
      })
      .catch((err) => toast.error(err instanceof ApiError ? err.message : "Failed to load draft"))
      .finally(() => setLoadingDraft(false));
    // editingDraftId only matters at mount — onClose recreates the modal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const parseRecipients = () =>
    to.split(",").map((s) => s.trim()).filter(Boolean).map((email) => ({ email }));

  const send = async () => {
    const recipients = parseRecipients();
    if (recipients.length === 0 || !subject || !text.trim()) { toast.error("Fill in all fields"); return; }
    setSending(true);
    try {
      if (draftThreadId) {
        // Promote a saved draft (whether opened-from-Drafts or just saved
        // mid-compose). The draft already has the latest body via PATCH on
        // Save Draft; but the user may have typed since last save — flush
        // a final PATCH first so the body that gets sent matches the UI.
        await api.patch(`/email/drafts/${draftThreadId}`, { to: recipients, subject, text, html });
        await api.post(`/email/drafts/${draftThreadId}/send`);
      } else {
        await api.post("/email/compose", { to: recipients, subject, text, html });
      }
      onSent();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const saveDraft = async () => {
    setSavingDraft(true);
    try {
      const recipients = parseRecipients();
      if (draftThreadId) {
        await api.patch(`/email/drafts/${draftThreadId}`, { to: recipients, subject, text, html });
      } else {
        const r = await api.post<{ thread_id: string }>("/email/drafts", { to: recipients, subject, text, html });
        setDraftThreadId(r.thread_id);
      }
      onDraftSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to save draft");
    } finally {
      setSavingDraft(false);
    }
  };

  const discardDraft = async () => {
    if (!draftThreadId) { onClose(); return; }
    if (!confirm("Discard this draft? This can't be undone.")) return;
    setDiscarding(true);
    try {
      await api.delete(`/email/drafts/${draftThreadId}`);
      onDraftDiscarded();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to discard");
    } finally {
      setDiscarding(false);
    }
  };

  // Insert a canned-response body into the compose area. The snippet body is
  // stored as plain text (with unresolved {{tokens}}), so we escape it and
  // convert newlines to <br> before appending to the HTML the RichEmailEditor
  // renders — and append the raw text to the plain-text mirror so both stay in
  // sync. The editor picks up the new `html` via its external-value effect.
  const insertSnippet = (rawBody: string) => {
    const escaped = rawBody
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    const snippetHtml = `<p>${escaped.replace(/\n/g, "<br>")}</p>`;
    setHtml((prev) => {
      const base = prev && prev.trim() && prev !== "<p></p>" ? prev : "";
      return base + snippetHtml;
    });
    setText((prev) => (prev.trim() ? `${prev}\n${rawBody}` : rawBody));
  };

  const title = editingDraftId ? "Edit draft" : "New message";

  return (
    <Modal open onClose={onClose} title={title} size="xl">
      {loadingDraft ? (
        <div className="space-y-3">
          <Skeleton className="h-9" />
          <Skeleton className="h-9" />
          <Skeleton className="h-80" />
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">To</label>
            <input
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="recipient@example.com (comma-separated for multiple)"
              className="w-full h-9 px-3 rounded-lg bg-zinc-900 border border-zinc-800 text-sm text-zinc-100 placeholder:text-zinc-500 focus:border-rose-500/50 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Subject</label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject"
              className="w-full h-9 px-3 rounded-lg bg-zinc-900 border border-zinc-800 text-sm text-zinc-100 placeholder:text-zinc-500 focus:border-rose-500/50 focus:outline-none"
            />
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] uppercase tracking-wider text-zinc-500">Message</span>
            <SnippetPicker channel="email" onInsert={insertSnippet} />
          </div>
          <RichEmailEditor
            value={html}
            onChange={(h, t) => { setHtml(h); setText(t); }}
            placeholder="Write your message — bold, lists, links, images all supported."
            minHeight="20rem"
          />
        </div>
      )}
      <ModalFooter>
        {draftThreadId && (
          <Button
            variant="ghost"
            onClick={() => void discardDraft()}
            disabled={discarding || sending || savingDraft}
            className="text-rose-300 hover:bg-rose-500/15 hover:text-rose-200"
          >
            <Trash2 className="h-3.5 w-3.5 mr-1.5" />
            {discarding ? "Discarding…" : "Discard"}
          </Button>
        )}
        <span className="text-[10px] text-zinc-500 mr-auto self-center hidden sm:inline">
          Sends via SyncSuite Services → your ESP
        </span>
        <Button variant="ghost" onClick={onClose} disabled={sending || savingDraft || discarding}>
          Close
        </Button>
        <Button
          variant="secondary"
          onClick={() => void saveDraft()}
          disabled={sending || savingDraft || discarding || loadingDraft}
        >
          {savingDraft
            ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Saving…</>
            : <><FileEdit className="h-3.5 w-3.5 mr-1.5" />Save draft</>}
        </Button>
        <Button
          variant="primary"
          onClick={() => void send()}
          disabled={sending || savingDraft || discarding || loadingDraft}
        >
          {sending
            ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Sending…</>
            : <><Send className="h-3.5 w-3.5 mr-1.5" />Send</>}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
